'use es6';

import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import UIFlex from 'UIComponents/layout/UIFlex';
import UISection from 'UIComponents/section/UISection';
import UITab from 'UIComponents/nav/UITab';
import UITabs from 'UIComponents/nav/UITabs';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { H1 } from 'UIComponents/elements/headings';
import PracticalExercise from 'academy-shared-js/records/PracticalExercise';
import Quiz from 'academy-shared-js/records/Quiz';
import ProfessorList from '../LessonPage/ProfessorList';
import TrackNavigatorContainer from '../../containers/Navigator/TrackNavigator/TrackNavigatorContainer';
import UsageTracker from '../../utils/UsageTracker';
import { device } from '../../constants/StyledComponentConstants';
import styled, { mediaQuery, css } from 'academy-common-lib/utils/Styling';
import { getHumanizedDuration } from '../../utils/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BASE_CLASS_NAME = 'track-introduction-information';
const MobileOnlyTabs = styled(UITabs).withConfig({
  displayName: "TrackIntroductionInformation__MobileOnlyTabs",
  componentId: "ufuqvr-0"
})(["@media screen and ", "{display:none;}"], device.not.tablet);
const DesktopOnlyProfessorList = styled(ProfessorList).withConfig({
  displayName: "TrackIntroductionInformation__DesktopOnlyProfessorList",
  componentId: "ufuqvr-1"
})(["padding-left:16px;padding-right:16px;@media screen and ", "{display:none;}"], device.tablet);
const NegativeMargin = styled.div.withConfig({
  displayName: "TrackIntroductionInformation__NegativeMargin",
  componentId: "ufuqvr-2"
})(["margin:-16px;"]);
const MobilePaddedUISection = styled(UISection).withConfig({
  displayName: "TrackIntroductionInformation__MobilePaddedUISection",
  componentId: "ufuqvr-3"
})(["@media screen and ", "{padding-left:10px;padding-right:10px;}", ""], device.tablet, mediaQuery('notTablet', css(["padding-left:16px;padding-right:16px;"])));
const TrackIntroductionInformation = ({
  track,
  trackVideoDurationInSeconds,
  trackContent,
  trackLessons
}) => {
  const videoLength = getHumanizedDuration(trackVideoDurationInSeconds);
  const numQuizzes = trackContent.count(content => content instanceof Quiz);
  const numPracticalExercises = trackContent.count(content => content instanceof PracticalExercise);
  const trackProfessors = trackLessons.flatMap(thisLesson => thisLesson && thisLesson.get('professors')).groupBy(professor => professor.get('id')).map(professor => professor.first()).toList();
  const handleOrganisationUrlClick = (professorId, organisation, organisationUrl) => () => {
    UsageTracker.track('interaction', {
      action: 'professor-organisation-click',
      contentType: 'COURSE',
      appearsIn: 'courses',
      organisation,
      organisationUrl,
      professorId,
      trackId: track.get('id')
    });
  };
  return /*#__PURE__*/_jsxs("div", {
    className: BASE_CLASS_NAME,
    children: [/*#__PURE__*/_jsx(MobilePaddedUISection, {
      children: /*#__PURE__*/_jsx(H1, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.tracks.aboutThisTrack"
        })
      })
    }), /*#__PURE__*/_jsx(MobilePaddedUISection, {
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "baseline",
        className: `${BASE_CLASS_NAME}__content-info-list`,
        direction: "row",
        justify: "start",
        wrap: "wrap",
        children: [/*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            "data-test-id": "track-intro-lesson-count",
            message: "learningCenter.tracks.lessons",
            options: {
              numLessons: track.get('lessonIds').count()
            }
          })
        }), /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            "data-test-id": "track-intro-track-duration",
            message: `learningCenter.tracks.totalVideoLengthDuration`,
            options: {
              videoLength
            }
          })
        }), !!numQuizzes && /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "learningCenter.tracks.quizzes",
            options: {
              numQuizzes
            }
          })
        }), !!numPracticalExercises && /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "learningCenter.tracks.practicalExercises",
            options: {
              numPracticalExercises
            }
          })
        })]
      })
    }), /*#__PURE__*/_jsx(MobilePaddedUISection, {
      children: track.get('description')
    }), /*#__PURE__*/_jsxs(UISection, {
      children: [/*#__PURE__*/_jsx(DesktopOnlyProfessorList, {
        className: `${BASE_CLASS_NAME}__professors`,
        handleOrganisationUrlClick: handleOrganisationUrlClick,
        i18nHeading: "learningCenter.tracks.trackProfessors",
        professors: trackProfessors
      }), /*#__PURE__*/_jsxs(MobileOnlyTabs, {
        defaultSelected: "lessons",
        children: [/*#__PURE__*/_jsx(UITab, {
          tabId: "lessons",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.lessonNavigator.title"
          }),
          children: /*#__PURE__*/_jsx(NegativeMargin, {
            children: /*#__PURE__*/_jsx(TrackNavigatorContainer, {})
          })
        }), /*#__PURE__*/_jsx(UITab, {
          tabId: "professors",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.professor.other"
          }),
          children: /*#__PURE__*/_jsx(ProfessorList, {
            className: `${BASE_CLASS_NAME}__professors`,
            handleOrganisationUrlClick: handleOrganisationUrlClick,
            i18nHeading: "learningCenter.tracks.trackProfessors",
            professors: trackProfessors
          })
        })]
      })]
    })]
  });
};
TrackIntroductionInformation.propTypes = {
  track: ImmutablePropTypes.map.isRequired,
  trackContent: ImmutablePropTypes.listOf(ImmutablePropTypes.record).isRequired,
  trackLessons: ImmutablePropTypes.listOf(ImmutablePropTypes.map).isRequired,
  trackVideoDurationInSeconds: PropTypes.number.isRequired
};
export default TrackIntroductionInformation;